"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.objIsPromise = exports.ReactiveMap = void 0;
var tslib_1 = require("tslib");
var ContextMap_1 = require("./ContextMap");
var ListenersMap_1 = require("./ListenersMap");
var ReactiveMap = /** @class */ (function () {
    function ReactiveMap(_a) {
        var _b = _a === void 0 ? {} : _a, allowedKeys = _b.allowedKeys, allowedContextKeys = _b.allowedContextKeys, onError = _b.onError;
        var _this = this;
        this._values = new Map();
        this._initialisedItems = new Map();
        this._items = new Map();
        this._listeners = new ListenersMap_1.ListenersMap();
        this._allowedKeys = new Set();
        this._allowedContextKeys = new Set();
        this.initialise = function (key, initialiser) {
            if (!_this._initialisedItems.has(key)) {
                _this._initialisedItems.set(key, new Promise(function (resolve) {
                    var item = _this._createItem(key);
                    item.loading = true;
                    _this._callListeners(key);
                    initialiser()
                        .then(function (value) {
                        item.loading = false;
                        _this._values.set(key, value);
                        _this._callListeners(key);
                        resolve(item);
                    })
                        .catch(function (error) {
                        item.error = error;
                        item.loading = false;
                        _this._callListeners(key);
                        resolve(item);
                    });
                }));
            }
            return _this._initialisedItems.get(key);
        };
        this.addMapContext = function (key, contextValue) {
            if (_this._allowedContextKeys.size && !_this._allowedContextKeys.has(key)) {
                throw new Error("Key: ".concat(String(key), " is not allowed in the context"));
            }
            if ((0, exports.objIsPromise)(contextValue))
                contextValue.catch(_this._onError);
            _this._context.set(key, contextValue);
            return _this;
        };
        this.getMapContext = function (key) {
            return _this._context.get(key);
        };
        // This should be something like #setValue, but tsconfig is targeting es5 currently
        this._setValue = function (key, value, options) {
            var isEqual = (options || {}).isEqual;
            var equalityFunc = typeof isEqual === 'function' ? isEqual : function (a, b) { return a === b; };
            var currentValue = _this._values.get(key);
            if (!equalityFunc(currentValue, value)) {
                _this._values.set(key, value);
                _this._callListeners(key);
            }
        };
        this._callListeners = function (key) {
            var _a;
            var currentItem = _this._items.get(key);
            if (currentItem) {
                (_a = _this._listeners.get(key)) === null || _a === void 0 ? void 0 : _a.forEach(function (listener) {
                    listener(currentItem.get(), currentItem.error);
                });
            }
        };
        // This should be something like #createItem, but tsconfig is targeting es5 currently
        this._createItem = function (key) {
            var currentItem = _this._items.get(key);
            if (currentItem) {
                return currentItem;
            }
            if (_this._allowedKeys.size && !_this._allowedKeys.has(key)) {
                throw new Error("Key: ".concat(String(key), " is not allowed"));
            }
            var item = {
                set: function (newValue, isEqual) {
                    _this._setValue(key, newValue, { isEqual: isEqual });
                    return item;
                },
                listen: function (listener) {
                    _this._listeners.add(key, listener);
                    return function () {
                        var keyListeners = _this._listeners.get(key);
                        keyListeners === null || keyListeners === void 0 ? void 0 : keyListeners.delete(listener);
                        if ((keyListeners === null || keyListeners === void 0 ? void 0 : keyListeners.size) === 0) {
                            _this._listeners.delete(key);
                        }
                    };
                },
                get: function () { return _this._values.get(key); },
                connect: function (contextKey, connector, onConnectedCallback) {
                    var contextPromise = _this._context.get(contextKey);
                    var cleanupPromise = contextPromise
                        .then(function (contextValue) {
                        var cleanup = connector(contextValue, item.set, item.get);
                        onConnectedCallback === null || onConnectedCallback === void 0 ? void 0 : onConnectedCallback(cleanup);
                        return cleanup;
                    })
                        .catch(_this._onError);
                    return function () {
                        return cleanupPromise
                            .then(function (cleanup) {
                            if (cleanup)
                                return cleanup();
                        })
                            .catch(_this._onError);
                    };
                },
                loading: false,
            };
            _this._items.set(key, item);
            return item;
        };
        this.item = function (key, _a) {
            var _b = _a === void 0 ? {} : _a, initialiser = _b.initialiser;
            if (!key) {
                throw new ReferenceError('Item key is required');
            }
            var item = _this._createItem(key);
            if (typeof initialiser === 'function' && !_this._initialisedItems.has(key)) {
                var result_1 = initialiser();
                _this._initialisedItems.set(key, new Promise(function (resolve) {
                    if ((0, exports.objIsPromise)(result_1)) {
                        item.loading = true;
                        _this._callListeners(key);
                        var init = function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                            var value, error_1;
                            return tslib_1.__generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        _a.trys.push([0, 2, 3, 4]);
                                        return [4 /*yield*/, result_1];
                                    case 1:
                                        value = _a.sent();
                                        this._values.set(key, value);
                                        return [3 /*break*/, 4];
                                    case 2:
                                        error_1 = _a.sent();
                                        item.error = error_1;
                                        return [3 /*break*/, 4];
                                    case 3:
                                        item.loading = false;
                                        this._callListeners(key);
                                        resolve(item);
                                        return [7 /*endfinally*/];
                                    case 4: return [2 /*return*/];
                                }
                            });
                        }); };
                        init();
                    }
                    else {
                        _this._setValue(key, result_1);
                        resolve(item);
                    }
                }));
            }
            return item;
        };
        this._allowedKeys = new Set(allowedKeys);
        this._allowedContextKeys = new Set(allowedContextKeys);
        this._context = new ContextMap_1.ContextMap();
        this._onError = onError;
    }
    Object.defineProperty(ReactiveMap.prototype, "allowedKeys", {
        get: function () {
            return this._allowedKeys;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ReactiveMap.prototype, "allowedContextKeys", {
        get: function () {
            return this._allowedContextKeys;
        },
        enumerable: false,
        configurable: true
    });
    return ReactiveMap;
}());
exports.ReactiveMap = ReactiveMap;
// See https://stackoverflow.com/questions/27746304/how-do-i-tell-if-an-object-is-a-promise
// NOTE: Read all answers/comments
var objIsPromise = function (obj) {
    return typeof (obj === null || obj === void 0 ? void 0 : obj.then) === 'function';
};
exports.objIsPromise = objIsPromise;
//# sourceMappingURL=ReactiveMap.js.map